/**
 * @module UseListInvalidation
 * @purpose Context-based list invalidation — allows mutation hooks to trigger refetch
 *          on sibling list hooks without prop-drilling.
 * @depends React createContext, useRef, useCallback, useMemo, useEffect
 * @dependedOnBy App.tsx (Provider mount), mutation hooks (useInvalidate), list hooks (useInvalidationSubscription)
 *
 * @rules
 * - Performance: subscriber map lives in a ref so subscribe/unsubscribe never triggers re-renders
 * - State: no React state; pure ref + context
 * - Errors: outside-provider calls warn-and-noop (never throw); logs once per session via console.warn (allowed — see file exemption note)
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactElement,
  type ReactNode,
} from 'react'

export type ListResourceKey =
  | 'collection_receipts'
  | 'collection_requests'
  | 'collection_targets'
  | 'case_endpoints'
  | 'ingestion_runs'
  | 'case_activity'
  | 'qc_runs'
  | 'qc_dashboard_alerts'

interface ListInvalidationContextValue {
  subscribe: (key: ListResourceKey, fn: () => void) => () => void
  invalidate: (keys: ListResourceKey[]) => void
}

const ListInvalidationContext = createContext<ListInvalidationContextValue | null>(null)

let warnedNoProvider = false

function warnNoProvider(hookName: string): void {
  if (!warnedNoProvider) {
    // eslint-disable-next-line no-console
    console.warn(
      `[ListInvalidation] ${hookName} called outside <ListInvalidationProvider> — silently no-op. ` +
        'Mount the provider near app root for production. This is expected in some tests.'
    )
    warnedNoProvider = true
  }
}

export function __resetWarnFlagForTests(): void {
  warnedNoProvider = false
}

interface ListInvalidationProviderProps {
  children: ReactNode
}

/**
 * Mounts near the app root. Owns a Map<ResourceKey, Set<RefetchFn>> in a ref
 * so subscribe/unsubscribe does not trigger re-renders.
 *
 * @behaviour Subscribers added during an invalidation call do not fire until the next invalidation.
 * @errors Never throws; no network calls.
 */
export function ListInvalidationProvider({
  children,
}: ListInvalidationProviderProps): ReactElement {
  const subscribersRef = useRef<Map<ListResourceKey, Set<() => void>>>(new Map())

  const subscribe = useCallback((key: ListResourceKey, fn: () => void) => {
    let set = subscribersRef.current.get(key)
    if (!set) {
      set = new Set()
      subscribersRef.current.set(key, set)
    }
    set.add(fn)
    const captured = set
    return () => {
      captured.delete(fn)
    }
  }, [])

  const invalidate = useCallback((keys: ListResourceKey[]) => {
    for (const key of keys) {
      const subs = subscribersRef.current.get(key)
      if (!subs) continue
      const snapshot = new Set(subs)
      for (const fn of snapshot) {
        fn()
      }
    }
  }, [])

  const value = useMemo<ListInvalidationContextValue>(
    () => ({ subscribe, invalidate }),
    [subscribe, invalidate]
  )

  return (
    <ListInvalidationContext.Provider value={value}>{children}</ListInvalidationContext.Provider>
  )
}

/**
 * Subscribes a list hook's `refetch` callback to a resource key. Re-subscribes
 * automatically when `refetch` changes (acceptable because list hooks return
 * stable refetch references via `useCallback`).
 *
 * Accepts async refetch functions — the returned Promise is intentionally
 * discarded (fire-and-forget) so the subscriber map stays synchronous.
 *
 * @behaviour No-op when called outside the provider — logs `console.warn` once per session.
 * @errors Never throws.
 */
export function useInvalidationSubscription(
  key: ListResourceKey,
  refetch: () => Promise<void> | void
): void {
  const ctx = useContext(ListInvalidationContext)
  useEffect(() => {
    if (!ctx) {
      warnNoProvider('useInvalidationSubscription')
      return
    }
    const syncWrapper = (): void => {
      void refetch()
    }
    return ctx.subscribe(key, syncWrapper)
  }, [ctx, key, refetch])
}

/**
 * Returns an `invalidate(keys)` function that mutation hooks call after a
 * successful operation.
 *
 * @behaviour Returns a no-op function when called outside the provider — logs `console.warn` once.
 * @errors Never throws.
 */
export function useInvalidate(): (keys: ListResourceKey[]) => void {
  const ctx = useContext(ListInvalidationContext)
  if (!ctx) {
    warnNoProvider('useInvalidate')
    return () => {
      /* no-op outside provider */
    }
  }
  return ctx.invalidate
}
